import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import UnmatchedInvoicesModal from '../pages/InvoicesPage/UnmatchedInvoicesModal';
import InvoiceMissingModal from '../pages/TransactionsPage/InvoiceMissingModal';
import routes from '../../routes/routes.json';
import {DEFAULT_MODAL_PROPS, popupSettingsConstants, systemHashKeysConstants} from '../../constants';
import {mainActions} from '../../state/actions/main.actions';
import {userActions} from '../../state/actions/user.actions';
import {locationHelpers, transactionsHelpers} from '../../utils/helpers';

const {OPEN_BANK_ACCOUNT_DETAILS, TRANSACTIONS_INVOICE_MISSING, UNMATCHED_INVOICES} = systemHashKeysConstants;

const HashWindowManager = ({
  children,
}) => {
  const navigate = useNavigate();
  const [unmatchedInvoicesModalProps, setUnmatchedInvoicesModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [transactionsInvoiceMissingModalProps, setTransactionsInvoiceMissingModalProps] = useState(DEFAULT_MODAL_PROPS);

  const dispatch = useDispatch();

  const { hash, search } = useLocation();

  const { companyState, employeeId, isAuthenticated, popupSettings } = useSelector(({banking, user}) => ({
    companyState: banking?.companyState,
    employeeId: user?.employee?.id,
    isAuthenticated: user?.isAuth,
    popupSettings: user?.popupSettings,
    user: user,
  }));

  const handleOpenModal = (setter, path) => {
    if (!isAuthenticated) return;

    setter?.({ ...DEFAULT_MODAL_PROPS, open: true });
    path && navigate(path);
  }

  useEffect(() => {
    if (hash) {
      const hashCallbacks = {
        [OPEN_BANK_ACCOUNT_DETAILS]: handleOpenBankAccountDetails,
        [TRANSACTIONS_INVOICE_MISSING]: handleTransactionsInvoiceMissing,
        [UNMATCHED_INVOICES]: handleUnmatchedInvoices,
      }
      if (hashCallbacks.hasOwnProperty(hash)) {
        hashCallbacks[hash](hash);
      }
    }
  }, [hash, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!popupSettings) return; // Exit if popupSettings is null/undefined

    for (const key in popupSettings) {
      if (popupSettings[key]) {
        const hashKey = Object.entries(popupSettingsConstants).find(([_, value]) => value === key)?.[0];
        const hashValue = systemHashKeysConstants[hashKey];

        if (hashValue) {
          navigate(`/${hashValue}`);
          return; // Exit early after first valid navigation
        }
      }
    }
  }, [popupSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelInvoiceMissingModal = () => setTransactionsInvoiceMissingModalProps(DEFAULT_MODAL_PROPS);

  const handleOkInvoiceMissingModal = () => {
    const filterParams = transactionsHelpers.getMissingInfoFilterParams(employeeId);
    const searchWithParams = locationHelpers.buildSearchWithParams(search, filterParams);

    handleCancelInvoiceMissingModal();
    navigate(`${routes.transactionsList}?${searchWithParams}`);
  }

  const handleCancelUnmatchedInvoicesModal = () => setUnmatchedInvoicesModalProps(DEFAULT_MODAL_PROPS);

  const handleTransactionsInvoiceMissing = () => {
    handleOpenModal(setTransactionsInvoiceMissingModalProps);
    updatePopupState([popupSettingsConstants.TRANSACTIONS_INVOICE_MISSING, popupSettingsConstants.UNMATCHED_INVOICES]);
  }

  const handleUnmatchedInvoices = () => {
    handleOpenModal(setUnmatchedInvoicesModalProps, `${routes.invoicesList}${search}#outstanding`);
    updatePopupState([popupSettingsConstants.UNMATCHED_INVOICES]);
  }

  const handleOpenBankAccountDetails = () => {
    dispatch(mainActions.displayWalletDetailsInitially());
    navigate('/');
  }

  const updatePopupState = (keys = []) => {
    dispatch(userActions.updateEmployeePopupSettings({
      data: {'popup_settings': keys},
      successCallback: dispatch(userActions.getEmployeePopupSettings())
    }));
  }

  const isOpen = (getter) => companyState && getter?.open;

  return (
    <>
      {children}

      <InvoiceMissingModal
        {...transactionsInvoiceMissingModalProps}
        onCancel={handleCancelInvoiceMissingModal}
        onOk={handleOkInvoiceMissingModal}
        open={isOpen(transactionsInvoiceMissingModalProps)}
      />

      <UnmatchedInvoicesModal
        {...unmatchedInvoicesModalProps}
        onCancel={handleCancelUnmatchedInvoicesModal}
        onOk={handleCancelUnmatchedInvoicesModal}
        open={isOpen(unmatchedInvoicesModalProps)}
      />
    </>
  );
};

export default HashWindowManager;
